import { useMemo, useState } from 'react';

import { collections, pageForKnowledgeId, pagesForCollection } from './data';
import { ArrowIcon } from './icons';
import { PageHeaderImage } from './PageHeaderImage';

import { Link } from './router';
import type { SiteLanguage, SitePage, SitePageSummary } from './types';

const articleKinds = new Set(['concept', 'procedure', 'story', 'place', 'tool']);
const isArticle = (page: SitePageSummary) => articleKinds.has(page.kind) && page.wordCount > 0;
const startingPoints = [
  { id: 'start-here', title: { en: 'Still figuring things out?', es: '¿Todavía estás explorando?' }, body: { en: 'A plain-language introduction to the choices and questions in this library.', es: 'Una introducción clara a las opciones y preguntas de esta biblioteca.' } },
  { id: 'plan-your-surgery', title: { en: 'Making a plan?', es: '¿Estás haciendo un plan?' }, body: { en: 'Work through consultations, documents, support, and your next practical step.', es: 'Organiza consultas, documentos, apoyo y tu siguiente paso práctico.' } },
  { id: 'caregivers', title: { en: 'Supporting someone?', es: '¿Acompañas a alguien?' }, body: { en: 'Find useful ways to help without taking over the person’s decisions.', es: 'Encuentra formas útiles de ayudar sin tomar decisiones por la otra persona.' } },
] as const;

function IndexRow({ page }: { page: SitePageSummary }) {
  return (
    <li className="topic-row">
      <Link className="topic-row__primary" href={page.route} aria-label={page.title}>
        <div><strong>{page.title}</strong><p>{page.description}</p></div>
        <span className="topic-row__meta">{page.readMinutes} {page.lang === 'es' ? 'min de lectura' : 'min read'}</span>
        <ArrowIcon />
      </Link>
    </li>
  );
}

export function CollectionPage({ page, pages }: { page: SitePage; pages: SitePageSummary[] }) {
  const language = page.lang === 'es' ? 'es' : 'en';
  const items = pagesForCollection(language, page.collection);
  return (
    <main id="main-content" className="index-page">
      <header className="index-hero shell">
        <nav className="breadcrumbs" aria-label="Breadcrumb"><Link href={pageForKnowledgeId(language, 'explorer')?.route ?? '/explore'}>{language === 'es' ? 'Explorar' : 'Explore'}</Link><span>/</span><span>{page.title}</span></nav>
        <div className="index-hero__content"><div><span className="eyebrow">{language === 'es' ? 'Colección' : 'Collection'}</span><h1>{page.title}</h1><p>{page.description}</p></div><PageHeaderImage page={page} priority /></div>
      </header>
      <section className="shell index-body">
        <div className="index-label"><span>{language === 'es' ? 'Tema' : 'Topic'}</span><span>{language === 'es' ? 'Tiempo de lectura' : 'Reading time'}</span></div>
        <ul className="topic-index" role="list" aria-label={`${page.title} ${language === 'es' ? 'temas' : 'topics'}`}>
          {items.map((item) => <IndexRow page={item} key={item.id} />)}
        </ul>
      </section>
    </main>
  );
}

export function ExplorerPage({ page, pages }: { page: SitePage; pages: SitePageSummary[] }) {
  const language = page.lang === 'es' ? 'es' : 'en';
  const [activeCollection, setActiveCollection] = useState('all');
  const articles = useMemo(() => pages.filter((candidate) => candidate.lang === language && isArticle(candidate)), [language, pages]);
  const visible = activeCollection === 'all' ? articles : articles.filter((candidate) => candidate.collection === activeCollection);

  return (
    <main id="main-content" className="explorer-page">
      <header className="explorer-hero shell"><div><span className="eyebrow">{language === 'es' ? 'Mapa de conocimiento' : 'Knowledge map'}</span><h1>{language === 'es' ? 'Explora el campo.' : 'Explore the field.'}</h1><p>{language === 'es' ? 'Empieza por una pregunta, un procedimiento o una historia. También puedes filtrar todos los temas a continuación.' : 'Start with a question, procedure, or story. You can also filter every topic below.'}</p></div><PageHeaderImage page={page} priority /></header>

      <section className="explorer-paths shell">
        <header><span className="eyebrow">{language === 'es' ? 'Por dónde empezar' : 'Places to begin'}</span><p>{language === 'es' ? 'Tres introducciones breves. Elige la que te sirva hoy.' : 'Three short introductions. Choose what helps today.'}</p></header>
        <nav aria-label={language === 'es' ? 'Por dónde empezar' : 'Places to begin'}>
          {startingPoints.map((point) => {
            const target = pageForKnowledgeId(language, point.id);
            return target && target.wordCount > 0 ? <Link key={point.id} href={target.route}><strong>{point.title[language]}</strong><p>{point.body[language]}</p><ArrowIcon /></Link> : null;
          })}
        </nav>
      </section>

      <section id="collections" className="explorer-field shell">
        <aside className="collection-filter" aria-label={language === 'es' ? 'Filtrar colecciones' : 'Filter collections'}>
          <button className={activeCollection === 'all' ? 'is-active' : ''} type="button" aria-pressed={activeCollection === 'all'} onClick={() => setActiveCollection('all')}>{language === 'es' ? 'Todo' : 'All topics'}</button>
          {collections.map((collection) => {
            return <button className={activeCollection === collection.id ? 'is-active' : ''} type="button" aria-pressed={activeCollection === collection.id} key={collection.id} onClick={() => setActiveCollection(collection.id)}>{collection.label[language]}</button>;
          })}
        </aside>
        <div className="explorer-results">
          <div className="index-label" role="status" aria-live="polite" aria-atomic="true"><span>{visible.length} {language === 'es' ? 'temas' : 'topics'}</span><span>{language === 'es' ? 'Explora por tema' : 'Browse by topic'}</span></div>
          <ul className="topic-index" role="list" aria-label={language === 'es' ? 'Todos los temas' : 'All topics'}>{visible.map((item) => <IndexRow page={item} key={item.id} />)}</ul>
        </div>
      </section>
    </main>
  );
}

const comparisonRows = {
  createsOrRemoves: { en: 'Creates or removes', es: 'Crea o elimina' },
  canal: { en: 'Canal', es: 'Canal' },
  donorSite: { en: 'Donor site', es: 'Zona donante' },
  standingUrination: { en: 'Standing urination', es: 'Orinar de pie' },
  staging: { en: 'Staging', es: 'Etapas' },
};

export function ComparisonPage({ page, pages }: { page: SitePage; pages: SitePageSummary[] }) {
  const language = page.lang === 'es' ? 'es' : 'en';
  const procedures = pages.filter((candidate) => candidate.lang === language && candidate.kind === 'procedure' && candidate.comparison);
  return (
    <main id="main-content" className="comparison-page">
      <header className="comparison-hero shell"><div><span className="eyebrow">{language === 'es' ? 'Vista comparativa' : 'Comparison view'}</span><h1>{language === 'es' ? 'Comparar procedimientos.' : 'Compare procedures.'}</h1><p>{language === 'es' ? 'Una orientación lado a lado. Abre cualquier procedimiento para leer la investigación completa, las variaciones y los riesgos.' : 'A side-by-side orientation. Open any procedure for the full research, variations, and risks.'}</p></div><PageHeaderImage page={page} priority /></header>
      <p id="comparison-scroll-help" className="comparison-scroll-help shell">{language === 'es' ? 'Desliza la tabla hacia un lado para ver todas las columnas. →' : 'Swipe the table sideways to see every column. →'}</p>
      <section className="shell comparison-table-wrap comparison-table-wrap--edge-cue" role="region" aria-label={language === 'es' ? 'Tabla comparativa desplazable' : 'Scrollable comparison table'} aria-describedby="comparison-scroll-help" tabIndex={0}>
        <table className="comparison-table" aria-label={language === 'es' ? 'Comparación de procedimientos' : 'Procedure comparison'}>
          <thead><tr><th scope="col">{language === 'es' ? 'Procedimiento' : 'Procedure'}</th>{Object.values(comparisonRows).map((label) => <th scope="col" key={label.en}>{label[language]}</th>)}</tr></thead>
          <tbody>{procedures.map((procedure) => {
            const values = procedure.comparison ?? {};
            return <tr key={procedure.id}><th scope="row"><Link href={procedure.route}>{procedure.title}</Link></th>{Object.keys(comparisonRows).map((key) => <td key={key}>{String(values[key] ?? '—')}</td>)}</tr>;
          })}</tbody>
        </table>
      </section>
      <aside className="comparison-note shell"><p>{language === 'es' ? 'Las técnicas, la elegibilidad, la anatomía y los resultados varían. Esta tabla orienta la lectura; no prescribe una decisión.' : 'Techniques, eligibility, anatomy, and outcomes vary. This table orients your reading; it does not prescribe a decision.'}</p></aside>
    </main>
  );
}
